import { API_BASE_URL } from "../config/config";
import { logEvent } from "../utils/driverLogger";

class ApiService {
  private static async request(
    endpoint: string,
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
    body?: any
  ) {
    const token = localStorage.getItem("_token");
    const isFormData = body instanceof FormData;

    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const config: RequestInit = {
      method,
      headers,
      ...(body && {
        body: isFormData ? body : JSON.stringify(body),
      }),
    };

    // Skip logging the log endpoint itself to avoid infinite loops
    const skipLog = endpoint.includes("/orders/driver-log");
    const started = Date.now();
    if (!skipLog) {
      logEvent("api_request", { endpoint, method, hasToken: !!token, isFormData });
    }

    let response = null;

    try {
      response = await fetch(API_BASE_URL + endpoint, config);
    } catch (networkError: any) {
      if (!skipLog) {
        logEvent("api_network_error", {
          endpoint,
          method,
          duration_ms: Date.now() - started,
          message: String(networkError?.message ?? networkError),
        });
      }
      throw new Error("Network error: " + networkError.message);
    }

    if (!response.ok) {
      if (!skipLog) {
        logEvent("api_response", {
          endpoint,
          method,
          status: response.status,
          ok: false,
          duration_ms: Date.now() - started,
        });
      }
      // Handle unauthorized/forbidden errors (401, 403)
      if (response.status === 401 || response.status === 403) {
        // Only redirect if we're not on the vendor access page
        // This prevents redirect loops when accepting new deliveries
        const currentPath = window.location.pathname;
        const isErrorPage = currentPath === "/403" || currentPath === "/404";
        const isVendorAccess = currentPath === "/driver-access";
        
        // Don't redirect if already on error page or vendor access
        if (!isErrorPage && !isVendorAccess) {
          // Check if token exists - if it does, might be temporary backend issue
          const token = localStorage.getItem("_token");
          if (!token) {
            // No token means truly unauthorized - clear and redirect
            localStorage.clear();
            window.location.href = "/403";
          }
          // If token exists, might be temporary backend delay - don't redirect immediately
        }
        throw new Error("Unauthorized access");
      }
      
      let errorText;
      try {
        errorText = await response.text();
      } catch {
        errorText = "Unknown error";
      }
      throw new Error(errorText || "API Request Failed!");
    }
    if (!skipLog) {
      logEvent("api_response", {
        endpoint,
        method,
        status: response.status,
        ok: true,
        duration_ms: Date.now() - started,
      });
    }
    return response.json();
  }


  
  static verifyVendorToken(token: string, deviceId?: string) {
    const url = deviceId 
      ? `/vendor/verify-token/${token}?device_id=${encodeURIComponent(deviceId)}`
      : `/vendor/verify-token/${token}`;
    return this.request(url, "GET");
  }

  static getVendorPOData(token: string) {
    return this.request(`/vendor/verify-po/${token}`, "GET");
  }

  static verifyVendorPO(token: string, isAdmin?: boolean) {
    const url = isAdmin ? `/vendor/verify-po/${token}?admin=true` : `/vendor/verify-po/${token}`;
    return this.request(url, "POST");
  }

  static verifyPin(data: { vendorToken: any; confirm_pin: string, token: boolean, device_id?: string}) {
    return this.request("/vendor/verify-pin", "POST", data);
  }

  static accessBySO(data: { so_number: string; pin: string; device_id?: string }) {
    return this.request("/vendor/access-by-so", "POST", data);
  }
  
  static getFreshData(data: {vendor_id?: any; sales_order_id: any, delivery_id?: any; token: boolean, device_id?: string}){
    return this.request("/orders/get-fresh-data", "POST", data);
  }

  static startDelivery(data: { delivery_id?: any; sales_order_id: any; vendor_id?: any; device_id?: any; driver_name: string; timezone?: string }) {
    return this.request("/orders/start-delivery", "POST", data);
  }

  // static materialLoaded(data: { delivery_id: any; vendor_id?: any }) {
  //   return this.request("/orders/material-loaded", "POST", data);
  // }
  // services/ApiService.ts
  
static materialLoaded(data: { 
  delivery_id: any; 
  vendor_id?: any; 
  picked_up_material?: string[] | null;  // NEW
}) {
  return this.request("/orders/material-loaded", "POST", data);
}
  static uploadFile(data: FormData) { 
    return this.request("/orders/upload-file", "POST", data);
  }

  static ReachedDestination(data: { delivery_id: any; vendor_id?: any }) {
    return this.request("/orders/reached", "POST", data);
  }
  
  static dumpPhotoReason(data: { delivery_id: any; reason: any; vendor_id?: any }) {
    return this.request("/orders/photo-reason", "POST", data);
  }

   static SubmitSignature(data: FormData) { 
    return this.request("/orders/submit-signature", "POST", data);
  }
   static completeDelivery(data: FormData) { 
    return this.request("/orders/complete-delivery", "POST", data);
  }

  static getProgressLoads(data: {sales_order_id: any; token: boolean}){
     return this.request("/orders/get-progress-loads", "POST", data);
  }

  static cancelDelivery(data: { delivery_id: any; vendor_id?: any; device_id?: any }) {
    return this.request("/orders/cancel-delivery", "POST", data);
  }

  
}

export default ApiService;
